import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/avif', 'image/webp']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const FILE_INPUT_ACCEPT = ACCEPTED_TYPES.join(',')

interface CoverImageUploadProps {
  onUpload: (file: File) => Promise<void>
  isUploading: boolean
  uploadProgress: number
}

export function CoverImageUpload({
  onUpload,
  isUploading,
  uploadProgress,
}: CoverImageUploadProps) {
  const { t } = useTranslation('common')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const validateAndUpload = useCallback(
    async (file: File) => {
      setError(null)

      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError('Please select a JPG, JPEG, PNG, GIF, AVIF, or WEBP image.')
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        setError('File size must be 5 MB or less.')
        return
      }

      try {
        await onUpload(file)
      } catch {
        setError('Upload failed. Please try again.')
      }
    },
    [onUpload],
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        void validateAndUpload(file)
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [validateAndUpload],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) {
        void validateAndUpload(file)
      }
    },
    [validateAndUpload],
  )

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        onChange={handleFileChange}
        className="hidden"
      />

      <div
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-3 py-10 border-2 border-dashed rounded-lg transition',
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
        )}
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Uploading... {uploadProgress}%
            </span>
            <div className="w-48 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-input rounded-md hover:bg-secondary transition"
            >
              <Upload className="size-4" />
              Choose file
            </button>
            <span className="text-sm text-muted-foreground">or</span>
            <span className="text-sm font-medium">{t('coverImage.dragAndDrop')}</span>
            <span className="text-xs text-muted-foreground">
              JPG, JPEG, PNG, GIF, AVIF or WEBP
            </span>
            <span className="text-xs text-muted-foreground">5 MB maximum size</span>
          </>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
